/**
 * Traffic-Dashboard der Betreiber-Konsole (0330, Wunsch des Vertriebs 16.10.2026): Zuordnung eines Website-Besuchs zu einem
 * Kanal. Gemeinsam für die Serverfunktion betrieb-traffic (Cloudflare Web Analytics, eigene Landungen mit utm-Parametern)
 * und die Oberfläche. Reine Funktionen ohne Abhängigkeiten.
 *
 * Cloudflare Web Analytics liefert je Seitenaufruf nur den Host der verweisenden Seite (refererHost), keine Adressparameter.
 * Kampagnen-Landungen (utm_*) zählt die Website deshalb selbst (site.js → Serverfunktion website-landung), siehe
 * kanalVonLandung.
 */

export const KANAELE = ["seo", "ki", "instagram", "email", "social", "direkt", "sonstige"] as const;
export type Kanal = (typeof KANAELE)[number];

/** Beschriftung je Kanal (Deutsch, Englisch) */
export const KANAL_NAMEN: Record<Kanal, [string, string]> = {
  seo: ["SEO", "SEO"],
  ki: ["KI-Suche", "AI search"],
  instagram: ["Instagram", "Instagram"],
  email: ["E-Mail-Akquise", "Email outreach"],
  social: ["Facebook/LinkedIn", "Facebook/LinkedIn"],
  direkt: ["Direkt", "Direct"],
  sonstige: ["Sonstige", "Other"],
};

/** Eigene Hosts: ein Aufruf von dort ist ein Seitenwechsel, kein neuer Besuch. */
export const EIGENE_HOSTS = ["creatoroffice.de", "www.creatoroffice.de", "app.creatoroffice.de"];
/** Hosts der Website (Besuche zählen nur hier; die App zählt ihre Seiten getrennt, z. B. /registrieren). */
export const WEBSITE_HOSTS = ["creatoroffice.de", "www.creatoroffice.de"];
/** Länder der eigenen Zugriffe (Mac des Inhabers in Portugal, Cloud-Sitzungen in den USA, Vertrieb in Spanien). */
export const INTERNE_LAENDER = ["PT", "US", "ES"];

/** Ländernamen aus Cloudflare (countryName ist dort der ISO-Code, z. B. „DE“); leer = unbekannt. */
export function istInternesLand(land: string | null | undefined): boolean {
  return !!land && INTERNE_LAENDER.includes(land.trim().toUpperCase());
}

const endetAuf = (host: string, domain: string) => host === domain || host.endsWith(`.${domain}`);
/** Suchmaschine unter einer Landesdomain (google.de, google.co.uk, bing.com, …) */
const suchmaschine = (host: string, name: string) => new RegExp(`(^|\\.)${name}\\.([a-z]{2,3})(\\.[a-z]{2})?$`).test(host);

/**
 * Kanal aus dem Host der verweisenden Seite. Eigene Hosts liefern null (Seitenwechsel). Leer = Direkt (Lesezeichen,
 * eingetippte Adresse, Apps ohne Referer, viele Mailprogramme).
 */
export function kanalVonReferer(refererHost: string | null | undefined): Kanal | null {
  const host = String(refererHost ?? "").trim().toLowerCase().replace(/^www\./, "").replace(/\.$/, "");
  if (!host) return "direkt";
  if (EIGENE_HOSTS.some((h) => h.replace(/^www\./, "") === host)) return null;
  // KI-Suche vor Suchmaschinen: gemini.google.com und copilot.microsoft.com sollen nicht als SEO zählen
  if (["chatgpt.com", "chat.openai.com", "openai.com", "perplexity.ai", "gemini.google.com", "bard.google.com", "copilot.microsoft.com", "claude.ai",
    "you.com", "phind.com"].some((d) => endetAuf(host, d)) || /^copilot\./.test(host)) return "ki";
  // Webmail vor Suchmaschinen (mail.google.com ist keine Google-Suche). Ohne utm zählt Webmail unter Sonstige; E-Mail-Akquise nur über
  // die utm-Parameter unserer Akquise-Mails (Entscheidung Inhaber 16.10.2026)
  if (/^(mail|webmail|outlook|email)\./.test(host) || ["outlook.live.com", "outlook.office.com", "outlook.office365.com", "gmx.net", "gmx.de", "web.de",
    "t-online.de", "mail.yahoo.com"].some((d) => endetAuf(host, d))) return "sonstige";
  if (["google", "bing", "duckduckgo", "ecosia", "yahoo", "startpage", "qwant", "yandex", "brave", "baidu"].some((n) => suchmaschine(host, n))
    || endetAuf(host, "search.brave.com") || endetAuf(host, "search.yahoo.com") || /^(www\.)?google\./.test(host)) return "seo";
  if (endetAuf(host, "instagram.com") || host === "l.instagram.com") return "instagram";
  if (["facebook.com", "fb.com", "fb.me", "linkedin.com", "lnkd.in", "com.linkedin.android", "messenger.com"].some((d) => endetAuf(host, d))) return "social";
  return "sonstige";
}

export interface Landung {
  pfad: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
}

/** Akquise-Mail: utm_medium=email, oder /start mit utm_source welle-* bzw. antwort-* (Links aus den Wellen und Antworten). */
export function istEmailLandung(l: Landung): boolean {
  const medium = String(l.utm_medium ?? "").trim().toLowerCase();
  const source = String(l.utm_source ?? "").trim().toLowerCase();
  const pfad = String(l.pfad ?? "").trim().toLowerCase().replace(/\.html$/, "").replace(/\/+$/, "");
  if (medium === "email" || medium === "e-mail" || medium === "mail") return true;
  return /^(\/en)?\/start$/.test(pfad) && /^(welle|antwort)-/.test(source);
}

/** Kanal einer Landung mit utm-Parametern; ohne eindeutigen Kampagnen-Kanal gilt der Referer. */
export function kanalVonLandung(l: Landung & { referer?: string | null }): Kanal | null {
  if (istEmailLandung(l)) return "email";
  const source = String(l.utm_source ?? "").trim().toLowerCase();
  if (source === "instagram" || source === "ig") return "instagram";
  if (source === "linkedin" || source === "facebook" || source === "fb") return "social";
  if (source === "chatgpt.com" || source === "chatgpt" || source === "perplexity") return "ki";
  return kanalVonReferer(l.referer);
}

/** /start der Website (auch /start.html, /start/, /en/start) */
export function istStartPfad(pfad: string | null | undefined): boolean {
  return /^(\/en)?\/start(\.html)?\/?$/.test(String(pfad ?? "").trim().toLowerCase());
}

export type KanalZahlen = Record<Kanal, number>;
export const leereKanaele = (): KanalZahlen => ({ seo: 0, ki: 0, instagram: 0, email: 0, social: 0, direkt: 0, sonstige: 0 });
